import {
  type FC,
  Fragment,
  type MouseEventHandler,
  type SVGProps,
  useCallback,
} from 'react'

import type { TeamMember } from '@api'
import upperFirst from 'lodash/upperFirst'

import { setMessageIdToFetch } from '@/models/chattingUI/messages/messages.ts'
import { useOpenChat } from '@/modules/chatting/hooks/use-open-chat.ts'
import type {
  Message,
  Notification,
  NotificationTypeBody,
} from '@/modules/chatting/types'
import { unescapeHtmlText } from '@/modules/chatting/ui/chat-window/fan-info/utils.ts'
import { TimeDifference } from '@/modules/chatting/ui/components/TimeDifference'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar'

import PurchaseBadgeIcon from '../../../../../icons/action-price-tag.svg'
import StarIcon from '../../../../../icons/star.svg'
import SubscriptionBadgeIcon from '../../../../../icons/subscription-badge.svg'
import { NotificationsReassignDropdown } from '../notifications-reassign-dropdown.tsx'
import { OpenChat } from './actions/open-chat'

interface Props {
  notification: Notification
  fan: NonNullable<Notification['user']>
  previousNotification?: Notification
  onCloseNotificationsBar: () => void
  members: TeamMember[]
  isMembersFetching: boolean
}

const badgeIconsMap: Partial<
  Record<NotificationTypeBody, FC<SVGProps<SVGElement>>>
> = {
  subscribed: SubscriptionBadgeIcon,
  paided_message: PurchaseBadgeIcon,
}

export const NotificationItem = ({
  members,
  isMembersFetching,
  notification,
  previousNotification,
  onCloseNotificationsBar,
  fan,
}: Props) => {
  const { type, text, createdAt } = notification

  const { handleOpenChat, isChatOpening } = useOpenChat()

  const openChat = useCallback(async () => {
    await handleOpenChat(fan.id)
    onCloseNotificationsBar()
  }, [fan.id, handleOpenChat, onCloseNotificationsBar])

  const openMessageInChat = (messageId: Message['id']) => {
    setMessageIdToFetch({ messageIdToFetch: messageId, fanId: fan.id })
    openChat()
    onCloseNotificationsBar()
  }

  const onLinkClick: MouseEventHandler<HTMLDivElement> = (event) => {
    const target = event.target as HTMLElement

    if (target.tagName === 'A') {
      const href = (target as HTMLAnchorElement).href

      if (type === 'paided_message') {
        const firstId = Number(new URL(href).searchParams.get('firstId'))

        if (firstId && !Number.isNaN(firstId)) {
          event.preventDefault()
          event.stopPropagation()
          openMessageInChat(firstId)

          return
        }
      }

      const regex = /https:\/\/onlyfans\.com\/u(\d+)$/
      const userIdMatch = href.match(regex)

      if (userIdMatch) {
        event.preventDefault()
        event.stopPropagation()

        openChat()
      }
    }
  }

  const BadgeIcon = badgeIconsMap[type]

  const htmlText = text.replaceAll('<a ', '<a target="_blank" ')

  return (
    <div className="flex gap-1.5">
      <div className="relative w-max pr-2">
        <CreatorAvatar
          size={44}
          avatarSrc={
            fan.avatarThumbs?.c50 ?? fan.avatarThumbs?.c144 ?? fan.avatar
          }
          name={fan.displayName ?? fan.name}
        />
        {BadgeIcon && (
          <div className="border-card bg-secondary-50 text-primary-500 dark:bg-secondary-600 absolute left-[26px] top-[26px] flex h-[26px] w-[26px] items-center justify-center rounded-full border-2">
            <BadgeIcon className="h-4 w-4" />
          </div>
        )}
      </div>
      <div className="max-w-full flex-1 space-y-3 overflow-hidden border-b pb-4">
        <div className="space-y-1">
          <p className="text-xs font-medium leading-[18px]">
            <span className="font-medium">
              {unescapeHtmlText(fan.displayName || fan.name)}
            </span>
            {fan.displayName && (
              <span className="text-secondary-400 dark:text-secondary-200">
                {' '}
                ({unescapeHtmlText(fan.name)})
              </span>
            )}
            <span className="text-secondary-400 dark:text-secondary-200 ml-1.5">
              @{fan.username}
            </span>
          </p>
          <div className="flex items-center gap-1 text-[11px]">
            <StarIcon className="fill-primary-500 text-primary-500 h-[14px] w-[14px] shrink-0" />
            <div className="no-scrollbar flex max-w-full gap-1 overflow-auto whitespace-nowrap text-xs font-medium">
              <span className="">Lists</span>
              {fan.listsStates?.map(({ id, name }) => (
                <Fragment key={id}>
                  <div className="text-[8px]">&#x2022;</div>
                  <span>{name}</span>
                </Fragment>
              ))}
            </div>
          </div>
          <div
            className="[&>a]:text-primary-500 overflow-hidden text-ellipsis whitespace-nowrap text-xs font-medium leading-[18px] [&>a]:outline-none"
            onClick={onLinkClick}
            dangerouslySetInnerHTML={{
              __html: upperFirst(htmlText),
            }}
          />
          <p className="flex items-center gap-1 text-[11px] leading-4">
            <span className="text-secondary-400 dark:text-secondary-200 whitespace-nowrap">
              <TimeDifference
                date={createdAt}
                interval={60 * 1000}
              />
            </span>
            <NotificationsReassignDropdown
              notification={notification}
              previousNotification={previousNotification}
              members={members}
              isMembersFetching={isMembersFetching}
            />
          </p>
        </div>
        <OpenChat
          openChat={openChat}
          isOpenChatLoading={isChatOpening}
        />
      </div>
    </div>
  )
}
